/**
 * @module loadUtils
 */
import { stringTemplateParser } from './stringTemplateParser.js';

/**
 * Fetches and parses a JSON file with robust error handling.
 * @param {string} url - The URL of the JSON file to fetch.
 * @returns {Promise<object>} The parsed JSON object.
 * @throws {Error} If the network request fails or JSON is invalid.
 */
async function fetchJson(url) {
  let response;
  try {
    response = await fetch(url);
    if (!response.ok) {
      // Throw an error for non-2xx responses (e.g., 404, 500)
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (err) {
    throw new Error(`Network error fetching ${url}: ${err.message}`);
  }

  try {
    return await response.json();
  } catch (err) {
    throw new Error(`Invalid JSON in ${url}: ${err.message}`);
  }
}

/**
 * Processes the metadata URL from the config, fetches metadata, and parses it
 * as template variables into the configuration string.
 * @param {object} config - The initial configuration object.
 * @param {string} language - The selected language ('en', 'es', etc.).
 * @returns {Promise<object>} The configuration object with metadata merged.
 */
async function processMetadata(config, language) {
  if (!config.metadataUrl) {
    config.metadata = {};
    return config;
  }

  // Check for a global metadataUrl override (for storm-specific data)
  // This code also allows us to use different metadata URLs for each language.
  if (typeof window.NHCMAP_GLOBAL?.metadataUrl !== 'undefined') {
    config.metadataUrl = typeof window.NHCMAP_GLOBAL.metadataUrl === 'object'
      ? window.NHCMAP_GLOBAL.metadataUrl[language]
      : window.NHCMAP_GLOBAL.metadataUrl;
  }

  const metadata = await fetchJson(config.metadataUrl);

  // Parse template variables throughout the entire configuration
  let configStr = JSON.stringify(config);
  configStr = stringTemplateParser(configStr, metadata);
  const processedConfig = JSON.parse(configStr);

  // Attach the resolved metadata to the config object for reference
  processedConfig.metadata = metadata;
  return processedConfig;
}

/**
 * Loads and processes a map configuration file, handling storm-specific
 * metadata. It NO LONGER handles translation directly.
 * @param {string} configUrl - The URL of the main product configuration file.
 * @param {string} [language='en'] - The desired language code (e.g., 'en').
 * @returns {Promise<object>} The processed configuration object with keys intact.
 */
export async function loadConfig(configUrl, language = 'en') {
  // Step 1: Fetch the main configuration.
  const baseConfig = await fetchJson(configUrl).catch(err => {
    console.error(`Failed to load configuration for ${configUrl}.`, err);
    throw err;
  });

  // Step 2: Process storm-specific metadata, replacing template variables.
  const configWithMetadata = await processMetadata(baseConfig, language);
  
  // Add the original URL to the config so we can find its language files later
  configWithMetadata.url = configUrl;

  // Step 3: Return the config with its translation keys still present.
  return configWithMetadata;
}

/**
 * Load an HTML file and return its content.
 * @param {string} url 
 * @returns {Promise<string>} The HTML content
 */
export async function loadHtml(url) {
  const response = await fetch(url);
  const html = await response.text();
  return html;
}

/**
 * Load a CSS file and append it to the head of the document.
 * @param {string} url 
 * @param {string} id - The ID to assign to the <link> element
 * @returns {Promise<void>}
 */
export function loadCss(url, id) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.id = id;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${url}`));
    document.head.appendChild(link);
  });
}

/**
 * Unload a CSS file by removing the <link> element with the specified ID.
 * @param {string} id - The ID of the <link> element to remove
 */
export function unloadCss(id) {
  const link = document.getElementById(id);
  if (link) {
    link.parentNode.removeChild(link);
  }
}